import { useMemo, useState } from "react";
import { archives, archiveCategories, type ArchiveRecord } from "@/mocks/archiveData";

const statusStyle: Record<string, string> = {
  可回溯: "bg-accent-100 text-accent-800 ring-accent-300/60",
  已删改: "bg-primary-100 text-primary-700 ring-primary-300/60",
  仅缓存: "bg-secondary-100 text-secondary-800 ring-secondary-300/60",
};

const hotTopics = ["微博热词", "综艺主页", "声明原文", "报道专题", "回应长文", "公告口径"];

export default function SnapshotBrowser() {
  const [category, setCategory] = useState("全部");
  const [keyword, setKeyword] = useState("");
  const [expanded, setExpanded] = useState<number | null>(null);

  const list = useMemo(() => {
    const kw = keyword.trim().toLowerCase();
    return archives.filter((a) => {
      const okCat = category === "全部" || a.category === category;
      if (!okCat) return false;
      if (!kw) return true;
      return [a.title, a.url, a.source, a.digest, a.category, ...a.tags].join(" ").toLowerCase().includes(kw);
    });
  }, [category, keyword]);

  const applyTopic = (t: string) => {
    setCategory("全部");
    setKeyword(t);
    setExpanded(null);
  };

  return (
    <section id="search" className="w-full scroll-mt-16 bg-background-100/70 py-14 md:py-20">
      <div className="mx-auto max-w-7xl px-4 md:px-6">
        <div className="max-w-2xl">
          <p className="text-sm font-medium tracking-wide text-primary-600">QUERY SNAPSHOT · 快速检索</p>
          <h2 className="mt-3 text-2xl text-foreground-950 md:text-4xl">查快照：一个入口，找回所有旧版页面</h2>
          <p className="mt-3 text-sm leading-6 text-foreground-700 md:text-base">
            输入网址或关键词，即可检索该页面收录过的每一次缓存版本与存档记录，并按时间顺序回溯查看。
          </p>
        </div>

        <div className="mt-8 flex flex-col gap-3 md:flex-row md:items-center">
          <div className="flex flex-1 items-center gap-2 rounded-lg border border-background-300 bg-background-50 px-3.5 py-3 focus-within:border-primary-400 focus-within:ring-2 focus-within:ring-primary-300/60">
            <i className="ri-search-line text-lg text-foreground-400"></i>
            <input
              id="archive-keyword"
              value={keyword}
              onChange={(e) => setKeyword(e.target.value)}
              placeholder="输入标题 / 网址 / 关键词筛选存档…"
              className="w-full bg-transparent text-sm text-foreground-950 outline-none placeholder:text-foreground-400"
            />
            {keyword && (
              <button type="button" onClick={() => setKeyword("")} aria-label="清空" className="text-foreground-400 hover:text-foreground-700">
                <i className="ri-close-circle-line text-lg"></i>
              </button>
            )}
          </div>
          <a
            href="#compare"
            className="whitespace-nowrap rounded-lg bg-secondary-500 px-5 py-3 text-center text-sm font-medium text-background-50 transition-colors hover:bg-secondary-600"
          >
            打开版本对比
          </a>
        </div>

        <div className="mt-6 flex flex-col gap-4 lg:flex-row lg:items-center lg:justify-between">
          <div className="flex flex-wrap items-center gap-2">
            {archiveCategories.map((c) => {
              const active = category === c;
              return (
                <button
                  key={c}
                  type="button"
                  onClick={() => {
                    setCategory(c);
                    setExpanded(null);
                  }}
                  className={`whitespace-nowrap rounded-full px-4 py-1.5 text-sm transition-colors ${
                    active
                      ? "bg-primary-500 text-background-50"
                      : "bg-background-50 text-foreground-700 ring-1 ring-background-300 hover:bg-secondary-100"
                  }`}
                >
                  {c}
                </button>
              );
            })}
          </div>
          <div className="flex flex-wrap items-center gap-2">
            <span className="whitespace-nowrap text-xs text-foreground-500">热门方向：</span>
            {hotTopics.map((t) => (
              <button
                key={t}
                type="button"
                onClick={() => applyTopic(t)}
                className="whitespace-nowrap rounded-full bg-accent-100 px-3 py-1 text-xs text-accent-800 transition-colors hover:bg-accent-200"
              >
                {t}
              </button>
            ))}
          </div>
        </div>

        <p className="mt-6 text-sm text-foreground-600">
          共匹配到 <strong className="font-semibold text-foreground-950">{list.length}</strong> 条存档记录
          {keyword && (
            <>
              ，关键词「<span className="text-primary-600">{keyword}</span>」
            </>
          )}
        </p>

        {list.length === 0 ? (
          <div className="mt-6 flex flex-col items-center justify-center rounded-2xl border border-dashed border-background-300 bg-background-50 py-16 text-center">
            <i className="ri-file-search-line text-5xl text-foreground-300"></i>
            <p className="mt-4 text-base font-medium text-foreground-800">暂未检索到匹配的存档记录</p>
            <p className="mt-1 text-sm text-foreground-600">换个关键词或清除分类筛选后再试，也可以联系我们提交页面纳入追溯。</p>
            <button
              type="button"
              onClick={() => {
                setCategory("全部");
                setKeyword("");
              }}
              className="mt-5 whitespace-nowrap rounded-md bg-secondary-500 px-5 py-2.5 text-sm text-background-50 hover:bg-secondary-600"
            >
              清除筛选
            </button>
          </div>
        ) : (
          <div className="mt-6 grid grid-cols-1 gap-4 sm:grid-cols-2 lg:grid-cols-3">
            {list.map((a) => (
              <ArchiveCard
                key={a.id}
                item={a}
                open={expanded === a.id}
                onToggle={() => setExpanded((cur) => (cur === a.id ? null : a.id))}
              />
            ))}
          </div>
        )}
      </div>
    </section>
  );
}

function ArchiveCard({
  item,
  open,
  onToggle,
}: {
  item: ArchiveRecord;
  open: boolean;
  onToggle: () => void;
}) {
  const versions = item.status === "已删改" ? 5 : 3;

  return (
    <article className="flex flex-col rounded-2xl border border-background-200 bg-background-50 p-5 transition-colors hover:border-background-300">
      <div className="flex items-center justify-between gap-2">
        <span className="rounded-full bg-secondary-100 px-2.5 py-1 text-xs text-secondary-800">{item.category}</span>
        <span className={`whitespace-nowrap rounded-full px-2.5 py-1 text-xs ring-1 ${statusStyle[item.status]}`}>
          {item.status}
        </span>
      </div>

      <h3 className="mt-4 text-base font-medium leading-6 text-foreground-950">{item.title}</h3>
      <p className="mt-1 truncate text-xs text-foreground-400">{item.url}</p>

      <p className="mt-3 line-clamp-2 text-sm leading-6 text-foreground-600">{item.digest}</p>

      <div className="mt-4 flex items-center justify-between text-xs text-foreground-500">
        <span className="flex items-center gap-1">
          <i className="ri-calendar-line"></i> 首见 {item.firstSeen}
        </span>
        <span className="flex items-center gap-1">
          <i className="ri-stack-line"></i> {item.snapCount} 份快照
        </span>
      </div>

      <div className="mt-3">
        <div className="flex items-center justify-between text-xs">
          <span className="text-foreground-600">累计变化幅度</span>
          <span className="font-medium text-primary-600">{item.changePct}%</span>
        </div>
        <div className="mt-1.5 h-1.5 overflow-hidden rounded-full bg-secondary-100">
          <div
            className={`h-full rounded-full ${item.changePct > 40 ? "bg-primary-500" : "bg-accent-500"}`}
            style={{ width: `${Math.min(item.changePct, 100)}%` }}
          ></div>
        </div>
      </div>

      <div className="mt-4 flex flex-wrap items-center gap-2">
        {item.tags.map((t) => (
          <span key={t} className="rounded bg-background-100 px-2 py-0.5 text-xs text-foreground-600">
            #{t}
          </span>
        ))}
      </div>

      <button
        type="button"
        onClick={onToggle}
        className="mt-5 flex w-full items-center justify-center gap-2 whitespace-nowrap rounded-md border border-background-300 bg-background-100 px-4 py-2.5 text-sm text-foreground-800 transition-colors hover:border-primary-300 hover:text-primary-700"
      >
        {open ? <i className="ri-arrow-up-s-line"></i> : <i className="ri-time-line"></i>}
        {open ? "收起时间线" : "查看历史时间线"}
      </button>

      {open && (
        <ol className="mt-4 space-y-2 border-t border-background-200 pt-4">
          {Array.from({ length: versions }).map((_, i) => (
            <li key={i} className="flex items-center gap-3 text-xs">
              <span className="h-2 w-2 shrink-0 rounded-full bg-accent-500"></span>
              <div className="flex min-w-0 flex-1 flex-col">
                <span className="font-medium text-foreground-800">
                  {String(item.snapCount - i * 2).padStart(2, "0")} 号快照
                </span>
                <span className="truncate text-foreground-500">
                  {i === 0 ? "最新缓存版本 · " : "历史版本 · "}页面处于可读取状态
                </span>
              </div>
              <span className="whitespace-nowrap text-foreground-500">
                {i === 0 ? "刚刚" : `${(i + 1) * 7} 小时前`}
              </span>
            </li>
          ))}
          <li className="rounded-lg bg-secondary-100/70 px-3 py-2.5 text-xs leading-5 text-secondary-900">
            <i className="ri-info-i mr-1"></i>
            {item.status === "可回溯"
              ? "该页面旧版内容已完整留痕，可通过时间点对照查看原始状态。"
              : item.status === "已删改"
                ? "检测到多轮删改，早期原文已保存在快照库，支持逐版本对比找回。"
                : "原页面已下线，当前仅能通过缓存版本查看存档正文。"}
          </li>
        </ol>
      )}
    </article>
  );
}